import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarCircleOutlined,
  PlusCircleOutlined,
  ShoppingCartOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { Button, Card, Col, Form, Row, Table, Tag } from 'antd';
import {
  AButton,
  ASelect,
  PageIntro,
  PageLayout,
  PagePanel,
  TreatmentRetailProductsModal,
} from '../../components';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import { getSelectClients, getSelectPaymentsMethods } from '../../http';
import { postProductQuickSale } from '../../http/products';
import {
  IClient,
  IFinancial,
  IPaymentItemInsert,
  IPaymentMethod,
} from '../../shared/interfaces';
import { currency } from '../../shared/utils/functions';
import InsertPayment from '../../components/InsertPayment';
import { IRetailSaleProduct } from '../../components/TreatmentRetailProductsModal';
import FinancialReceipt from '../Financials/Receipt';
import { Screens } from '../../shared/enums/screens.enum';

const QuickSalePage: React.FC = () => {
  const navigate = useNavigate();
  const { screenFind } = useAuth();
  const { addToast } = useToast();
  const [clients, setClients] = useState<IClient[]>([]);
  const [paymentsMethods, setPaymentsMethods] = useState<IPaymentMethod[]>([]);
  const [clientId, setClientId] = useState<string | null>(null);
  const [items, setItems] = useState<IRetailSaleProduct[]>([]);
  const [payments, setPayments] = useState<IPaymentItemInsert[]>([]);
  const [modalProducts, setModalProducts] = useState(false);
  const [modalPayment, setModalPayment] = useState(false);
  const [modalReceipt, setModalReceipt] = useState(false);
  const [paymentEdit, setPaymentEdit] = useState<IPaymentItemInsert | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [receiptRows, setReceiptRows] = useState<IFinancial[]>([]);

  const totalSale = useMemo(
    () => items.reduce((sum, item) => sum + Number(item.total_price || 0), 0),
    [items],
  );
  const totalPayments = useMemo(
    () => payments.reduce((sum, item) => sum + Number(item.value || 0), 0),
    [payments],
  );
  const remaining = totalSale - totalPayments;
  const change = totalPayments > totalSale ? totalPayments - totalSale : 0;
  const selectedClient =
    clients.find((client) => client.id === clientId) ?? null;

  const formatClientLabel = useCallback((client: IClient) => {
    const document = String(client.cpf_cnpj || '').trim();

    return document
      ? `${client.corporate_name} - ${document}`
      : client.corporate_name;
  }, []);

  const loadDependencies = useCallback(async () => {
    try {
      const [clientsData, paymentsData] = await Promise.all([
        getSelectClients(1),
        getSelectPaymentsMethods(),
      ]);
      setClients(clientsData);
      setPaymentsMethods(paymentsData);
    } catch {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao carregar dados da venda rápida.',
      });
    }
  }, [addToast]);

  useEffect(() => {
    loadDependencies();
  }, [loadDependencies]);

  const resetForm = useCallback(() => {
    setClientId(null);
    setItems([]);
    setPayments([]);
    setPaymentEdit(null);
  }, []);

  const handleRemovePayment = useCallback((id?: string) => {
    setPayments((state) => state.filter((item) => item.id !== id));
  }, []);

  const handleOpenPayment = useCallback((item: IPaymentItemInsert | null) => {
    setPaymentEdit(item);
    setModalPayment(true);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!clientId) {
      addToast({
        type: 'warning',
        title: 'Cliente obrigatório',
        description: 'Selecione o cliente para registrar a venda.',
      });
      return;
    }

    if (!items.length) {
      addToast({
        type: 'warning',
        title: 'Sem itens',
        description: 'Adicione ao menos um produto na venda.',
      });
      return;
    }

    if (!payments.length || remaining > 0.05) {
      addToast({
        type: 'warning',
        title: 'Pagamentos inconsistentes',
        description:
          'O total dos pagamentos precisa cobrir a venda antes de concluir.',
      });
      return;
    }

    try {
      setSubmitting(true);
      const response = await postProductQuickSale({
        client_id: clientId,
        items,
        payments: payments.map((item) => ({
          payment_id: item.payment_id,
          installment: item.installment,
          value: item.value,
          due_date: item.due_date,
        })),
      });

      const enrichedFinancials = (response.financials ?? []).map(
        (financial) => ({
          ...financial,
          client_name: selectedClient?.corporate_name,
          client_document: selectedClient?.cpf_cnpj,
          method_payment:
            paymentsMethods.find((item) => item.id === financial.payment)
              ?.description ?? financial.payment,
          payment_date: financial.due_date,
        }),
      );

      setReceiptRows(enrichedFinancials);
      setModalReceipt(true);
      addToast({
        type: 'success',
        title: 'Venda registrada',
        description: 'A venda rápida foi registrada com estoque e financeiro.',
      });
      resetForm();
    } catch (error: any) {
      console.log({ error });
      addToast({
        type: 'error',
        title: 'Erro',
        description:
          error.response?.data?.message ?? 'Erro ao registrar venda rápida.',
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    addToast,
    clientId,
    items,
    payments,
    paymentsMethods,
    remaining,
    resetForm,
    selectedClient,
  ]);

  return (
    <PageLayout>
      <PageIntro
        badge="Caixa"
        icon={<DollarCircleOutlined />}
        title="Venda de produtos"
        description="Registre vendas avulsas com baixa automática no estoque e lançamento financeiro no mesmo fechamento."
        actions={
          screenFind([Screens.salesView]) ? (
            <Button
              icon={<ShoppingCartOutlined />}
              onClick={() => navigate('/sales')}
            >
              Acompanhar vendas
            </Button>
          ) : undefined
        }
      />
      <PagePanel>
        <Card>
          <Form layout="vertical">
            <Row gutter={16} align="top">
              <Col xs={24} lg={16}>
                <Form.Item label="Cliente" required style={{ marginBottom: 0 }}>
                  <ASelect
                    showSearch
                    placeholder="Busque por nome ou CPF/CNPJ"
                    value={clientId}
                    onChange={setClientId}
                    filterOption={(input, option: any) =>
                      (option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={clients.map((client) => ({
                      label: formatClientLabel(client),
                      value: client.id,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} lg={8}>
                <Form.Item label=" " style={{ marginBottom: 0 }}>
                  <div style={{ width: '100%', maxWidth: 240 }}>
                    <Button
                      type="primary"
                      icon={<ShoppingCartOutlined />}
                      onClick={() => setModalProducts(true)}
                      style={{ width: '100%', height: 46 }}
                    >
                      Montar venda
                    </Button>
                  </div>
                </Form.Item>
              </Col>
            </Row>
            {selectedClient && (
              <Row>
                <Col span={24}>
                  <Tag color="blue">
                    Cliente: {selectedClient.corporate_name}
                  </Tag>
                  {selectedClient.cpf_cnpj ? (
                    <Tag>{selectedClient.cpf_cnpj}</Tag>
                  ) : null}
                  {selectedClient.cell_phone ? (
                    <Tag>{selectedClient.cell_phone}</Tag>
                  ) : null}
                </Col>
              </Row>
            )}
          </Form>
        </Card>
      </PagePanel>

      <PagePanel>
        <Card
          title="Itens da venda"
          extra={<Tag color="blue">{items.length} item(ns)</Tag>}
        >
          <Table
            rowKey="id"
            pagination={false}
            dataSource={items}
            locale={{ emptyText: 'Nenhum produto adicionado.' }}
            columns={[
              {
                title: 'Produto',
                dataIndex: 'product_name',
                render: (_: string, record: IRetailSaleProduct) =>
                  record.product_name
                    ? `${record.product_code} - ${record.product_name}`
                    : record.product_id,
              },
              { title: 'Qtd.', dataIndex: 'quantity', width: 90 },
              {
                title: 'Valor UN.',
                dataIndex: 'unit_price',
                width: 140,
                render: (value: number) => currency(value),
              },
              {
                title: 'Total',
                dataIndex: 'total_price',
                width: 140,
                render: (value: number) => currency(value),
              },
            ]}
          />
        </Card>
      </PagePanel>

      <PagePanel>
        <Card
          title="Pagamentos"
          extra={
            <Button
              type="primary"
              icon={<PlusCircleOutlined />}
              onClick={() => handleOpenPayment(null)}
            >
              Inserir pagamento
            </Button>
          }
        >
          <Table
            rowKey="id"
            pagination={false}
            dataSource={payments}
            locale={{ emptyText: 'Nenhum pagamento informado.' }}
            columns={[
              {
                title: 'Pagamento',
                dataIndex: 'payment_id',
                render: (value: string) =>
                  paymentsMethods.find((item) => item.id === value)
                    ?.description ?? value,
              },
              { title: 'Parcelas', dataIndex: 'installment', width: 100 },
              {
                title: 'Valor',
                dataIndex: 'value',
                width: 140,
                render: (value: number) => currency(value),
              },
              {
                title: 'Ações',
                width: 190,
                render: (item: IPaymentItemInsert) => (
                  <Row gutter={8}>
                    <Col>
                      <Button onClick={() => handleOpenPayment(item)}>
                        Editar
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemovePayment(item.id)}
                      />
                    </Col>
                  </Row>
                ),
              },
            ]}
          />
        </Card>
      </PagePanel>

      <PagePanel>
        <Card title="Resumo do fechamento">
          <Row gutter={16}>
            <Col xs={24} md={8}>
              Total da venda: <strong>{currency(totalSale)}</strong>
            </Col>
            <Col xs={24} md={8}>
              Total dos pagamentos: <strong>{currency(totalPayments)}</strong>
            </Col>
            <Col xs={24} md={8}>
              Saldo: <strong>{currency(remaining)}</strong>
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col xs={24} md={8}>
              Troco previsto: <strong>{currency(change)}</strong>
            </Col>
            <Col xs={24} md={16}>
              {change > 0 ? (
                <Tag color="green">
                  O pagamento excede a venda e o troco previsto sera de{' '}
                  {currency(change)}.
                </Tag>
              ) : (
                <Tag color={remaining > 0.05 ? 'orange' : 'blue'}>
                  {remaining > 0.05
                    ? 'Ainda falta complementar o pagamento.'
                    : 'Pagamento fechado sem troco.'}
                </Tag>
              )}
            </Col>
          </Row>
          <Row justify="end" style={{ marginTop: 16 }}>
            <AButton
              type="primary"
              onClick={handleSubmit}
              disabled={submitting}
            >
              Concluir venda rápida
            </AButton>
          </Row>
        </Card>
      </PagePanel>

      <TreatmentRetailProductsModal
        open={modalProducts}
        onClose={() => setModalProducts(false)}
        items={items}
        setItems={setItems}
      />
      <InsertPayment
        open={modalPayment}
        close={() => setModalPayment(false)}
        paymentItemInsert={paymentEdit}
        setPaymentsItemsInsert={setPayments}
        paymentsMethods={paymentsMethods}
        remaining={Math.max(remaining, 0.01)}
      />
      <FinancialReceipt
        open={modalReceipt}
        close={() => setModalReceipt(false)}
        selectRows={receiptRows}
      />
    </PageLayout>
  );
};

export default QuickSalePage;
